import React, { useState, useEffect } from 'react'
import { Form, Input, InputNumber, Select, DatePicker, Row, Col, Divider, Alert, Space, AutoComplete } from 'antd'
import { DollarOutlined } from '@ant-design/icons'
import moment from 'moment'
import { BillingEnums } from '../../utils/enums'
import { getPatientBalance, searchPatients } from '../../backend-integration/api/paymentAllocation'

const { Option } = Select
const { TextArea } = Input

const RefundForm = ({ form, preSelectedPatient, onFinish }) => {
  const [patientBalance, setPatientBalance] = useState(null)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [patientOptions, setPatientOptions] = useState([])

  const patientId = Form.useWatch('patientId', form)

  // Pre-select patient if provided
  useEffect(() => {
    if (preSelectedPatient && !patientId) {
      form.setFieldsValue({
        patientId: preSelectedPatient._id,
        patientName: preSelectedPatient.name,
        billingId: preSelectedPatient.billingId
      })
      fetchPatientBalance(preSelectedPatient._id)
    }
  }, [preSelectedPatient, form, patientId])

  useEffect(() => {
    if (!patientId) {
      setPatientBalance(null)
    }
  }, [patientId])

  const fetchPatientBalance = async (patientId) => {
    setLoadingBalance(true)
    try {
      const balance = await getPatientBalance(patientId)
      setPatientBalance(balance)
    } catch (error) {
      console.error('Error fetching patient balance:', error)
    } finally {
      setLoadingBalance(false)
    }
  }

  const handlePatientSearch = async (value) => {
    if (!value || value.length < 3) {
      setPatientOptions([])
      return
    }

    try {
      const patients = await searchPatients(value)
      setPatientOptions(
        (patients || []).map((patient) => ({
          value: patient._id,
          label: `${patient.name} - ${patient.billingId || patient.fileNo || 'N/A'}`,
          patient
        }))
      )
    } catch (error) {
      console.error('Error searching patients:', error)
      setPatientOptions([])
    }
  }

  const handlePatientSelect = (value, option) => {
    const patient = option.patient
    form.setFieldsValue({
      patientId: patient._id,
      patientName: patient.name,
      billingId: patient.billingId
    })
    fetchPatientBalance(patient._id)
  }

  const validateAmount = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter an amount'))
    }
    if (value <= 0) {
      return Promise.reject(new Error('Amount must be greater than 0'))
    }
    if (patientBalance && patientBalance.creditBalance < value) {
      return Promise.reject(
        new Error(`Refund amount cannot exceed credit balance of R${patientBalance.creditBalance.toFixed(2)}`)
      )
    }
    return Promise.resolve()
  }

  const getBankDetails = (paymentMethod) => {
    if (paymentMethod === BillingEnums.paymentMethods.EFT) {
      return (
        <>
          <Divider>Bank Details</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='bankName'
                label='Bank Name'
                rules={[{ required: true, message: 'Please enter bank name' }]}
              >
                <Input placeholder='e.g., Standard Bank' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='accountNumber'
                label='Account Number'
                rules={[{ required: true, message: 'Please enter account number' }]}
              >
                <Input placeholder='Account number' />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='accountHolder'
                label='Account Holder'
                rules={[{ required: true, message: 'Please enter account holder name' }]}
              >
                <Input placeholder='Name on account' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='branchCode'
                label='Branch Code'
                rules={[{ required: true, message: 'Please enter branch code' }]}
              >
                <Input placeholder='Branch code' />
              </Form.Item>
            </Col>
          </Row>
        </>
      )
    }
    return null
  }

  const paymentMethod = Form.useWatch('paymentMethod', form)

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{
        date: moment(),
        refundType: 'patient',
        paymentMethod: BillingEnums.paymentMethods.EFT
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='date' label='Refund Date' rules={[{ required: true, message: 'Please select date' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='refundType' label='Refund Type' rules={[{ required: true }]}>
            <Select>
              <Option value='patient'>Patient Refund</Option>
              <Option value='medical_aid'>Medical Aid Refund</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {!preSelectedPatient && (
        <>
          <Divider>Patient Details</Divider>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label='Search Patient'
                name='patientSearch'
                rules={[{ required: !preSelectedPatient, message: 'Please select a patient' }]}
              >
                <AutoComplete
                  options={patientOptions}
                  onSearch={handlePatientSearch}
                  onSelect={handlePatientSelect}
                  placeholder='Search by name, billing ID, or file number'
                  style={{ width: '100%' }}
                >
                  <Input.Search />
                </AutoComplete>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {preSelectedPatient && (
        <>
          <Divider>Patient Details</Divider>
          <Alert
            message='Selected Patient'
            description={
              <div>
                <strong>{preSelectedPatient.name}</strong>
                <br />
                Billing ID: {preSelectedPatient.billingId}
                {preSelectedPatient.fileNo && (
                  <>
                    <br />
                    File Number: {preSelectedPatient.fileNo}
                  </>
                )}
              </div>
            }
            type='info'
            showIcon
            style={{ marginBottom: 16 }}
          />
        </>
      )}

      <Form.Item name='patientId' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='patientName' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='billingId' hidden>
        <Input />
      </Form.Item>

      {patientBalance && (
        <Alert
          message='Account Balance'
          description={
            <Space direction='vertical'>
              <div>Current Balance: R{patientBalance.currentBalance?.toFixed(2)}</div>
              <div>Credit Balance: R{patientBalance.creditBalance?.toFixed(2)}</div>
              <div>
                Last Payment:{' '}
                {patientBalance.lastPaymentDate ? moment(patientBalance.lastPaymentDate).format('YYYY-MM-DD') : 'N/A'}
              </div>
            </Space>
          }
          type={patientBalance.creditBalance > 0 ? 'success' : 'warning'}
          showIcon
          icon={<DollarOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Divider>Refund Details</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='amount' label='Refund Amount' rules={[{ required: true }, { validator: validateAmount }]}>
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/R\s?|(,*)/g, '')}
              precision={2}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='paymentMethod' label='Payment Method' rules={[{ required: true }]}>
            <Select>
              <Option value={BillingEnums.paymentMethods.CASH}>Cash</Option>
              <Option value={BillingEnums.paymentMethods.EFT}>EFT</Option>
              <Option value={BillingEnums.paymentMethods.CARD}>Card</Option>
              <Option value={BillingEnums.paymentMethods.CHEQUE}>Cheque</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {getBankDetails(paymentMethod)}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='reasonCode'
            label='Reason Code'
            rules={[{ required: true, message: 'Please select reason' }]}
          >
            <Select>
              <Option value='OVERPAYMENT'>Overpayment</Option>
              <Option value='DUPLICATE_PAYMENT'>Duplicate Payment</Option>
              <Option value='SERVICE_CANCELLED'>Service Cancelled</Option>
              <Option value='INSURANCE_ADJUSTMENT'>Insurance Adjustment</Option>
              <Option value='BILLING_ERROR'>Billing Error</Option>
              <Option value='OTHER'>Other</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='approvedBy'
            label='Approved By'
            rules={[{ required: true, message: 'Please enter approver name' }]}
          >
            <Input placeholder='Name of person approving' />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name='reason'
        label='Detailed Reason'
        rules={[{ required: true, message: 'Please enter detailed reason' }]}
      >
        <TextArea rows={3} placeholder='Provide detailed explanation for the refund' />
      </Form.Item>

      <Form.Item
        name='reference'
        label='Reference Number'
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              const paymentMethod = getFieldValue('paymentMethod')
              if (paymentMethod === BillingEnums.paymentMethods.EFT && (!value || value.trim().length === 0)) {
                return Promise.reject(new Error('Reference number is required for EFT refunds'))
              }
              return Promise.resolve()
            }
          })
        ]}
      >
        <Input placeholder='Payment or transaction reference (required for EFT)' />
      </Form.Item>
    </Form>
  )
}

export default RefundForm
